import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    CircularProgress,
    DialogContent,
    IconButton,
    SelectChangeEvent,
    FormHelperText,
} from '@mui/material'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import { H6 } from '../../../../layouts/TextStyles'
import CustomInput from '../../../../components/muiComponents/customInput'
import MultipleSelect from '../../../../components/muiComponents/multipleSelect/MultipleSelect'
import SingleSelect from '../../../../components/muiComponents/singleSelect'
import { teacherApiSlice } from '../../../../features/teacher/teacherSlice'
import { courseApiSlice } from '../../../../features/course/courseSlice'
import { requiredString, requiredId } from '../../../../utils/validation'
import { GroupState } from '../../../../features/types'
import { groupApiSlice } from '../../../../features/group/groupSlice'
import { tone, ui } from '../../../../utils/uiTokens'

interface Props {
    editGroup: GroupState
}

const styles = {
    error: {
        color: tone.bad.text,
        marginLeft: 2,
    },
}

const EditGroupDialog = ({ editGroup }: Props) => {
    const { formatMessage } = useIntl()
    const [open, setOpen] = useState(false)
    const { curators = [], isCuratorsFetching } =
        teacherApiSlice.useFetchAllTeachersQuery(
            {
                query: `page=${0}&size=${10000}`,
                roles: ['CURATOR', 'SUBJECT_HEAD'],
            },
            {
                skip: !open,
                selectFromResult: ({ data, isFetching }) => ({
                    curators: data?.data,
                    isCuratorsFetching: isFetching,
                }),
            },
        )
    const { data: courses = [], isFetching: isCoursesFetching } =
        courseApiSlice.useFetchAllCoursesQuery(undefined, { skip: !open })
    const [updateGroup, { isLoading: isUpdating }] =
        groupApiSlice.useUpdateGroupMutation()

    const group: GroupState = {
        id: editGroup?.id,
        title: editGroup?.title,
        languageEnum: editGroup?.languageEnum,
        courseId: editGroup?.courseId,
        curatorIds: editGroup?.curatorIds,
        subjects: [],
        studentCount: editGroup?.studentCount,
    }

    const validationSchema = yup.object().shape({
        title: requiredString({ formatMessage }),
        //curatorIds: requiredArray({ formatMessage }),
        courseId: requiredId({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: group,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            handleUpdateGroup()
        },
    })

    const handleUpdateGroup = async () => {
        try {
            await updateGroup(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            formik.resetForm()
            handleClose()
        } catch (error) {}
    }

    // Выбирать можно только менторов; кто уже не ментор, но ещё стоит в группе,
    // показываем отдельной подписанной строкой, чтобы его можно было снять
    const mentorItems = curators.filter((curator) => curator.role === 'CURATOR')
    const curatorMenuItems = [
        ...mentorItems,
        ...(formik.values.curatorIds ?? [])
            .filter((id) => !mentorItems.some((curator) => curator.id === id))
            .map((id) => {
                const person = curators.find((curator) => curator.id === id)
                return {
                    id,
                    name: person
                        ? `${person.firstName} ${person.lastName} (${formatMessage({ id: 'curatorNotMentor' })})`
                        : `#${id} (${formatMessage({ id: 'curatorDeleted' })})`,
                }
            }),
    ]

    const handleLanguageChange = (
        event: SelectChangeEvent<typeof formik.values.languageEnum>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('languageEnum', value)
    }

    const handleCuratorsChange = (
        event: SelectChangeEvent<typeof formik.values.curatorIds>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue(
            'curatorIds',
            typeof value === 'string' ? value.split(',') : value,
        )
    }

    const handleCourseChange = (
        event: SelectChangeEvent<typeof formik.values.courseId>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('courseId', value)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <div>
            <IconButton
                sx={{ background: ui.track }}
                onClick={() => setOpen(true)}
            >
                <img
                    src={EditIcon}
                    alt="edit"
                />
            </IconButton>

            <Dialog
                maxWidth={'md'}
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title">
                    {formatMessage({ id: 'editData' })}
                </DialogTitle>

                {isCoursesFetching || isCuratorsFetching ? (
                    <CircularProgress sx={{ ml: 3 }} />
                ) : (
                    <DialogContent>
                        <form onSubmit={formik.handleSubmit}>
                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'groupName' })}</H6>

                                <CustomInput
                                    id="title"
                                    name="title"
                                    placeholder={formatMessage({
                                        id: 'groupName',
                                    })}
                                    fullWidth
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    helperText={formik.errors.title}
                                    error={Boolean(formik.errors.title)}
                                    margin="dense"
                                />
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'curators' })}</H6>

                                <MultipleSelect
                                    id="curatorIds"
                                    name="curatorIds"
                                    placeholder={formatMessage({
                                        id: 'curators',
                                    })}
                                    value={formik.values.curatorIds}
                                    menuItems={curatorMenuItems}
                                    multiple={true}
                                    searchable={true}
                                    handleChange={handleCuratorsChange}
                                    handleBlur={formik.handleBlur}
                                    handleError={Boolean(
                                        formik.errors.curatorIds,
                                    )}
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.curatorIds &&
                                        formik.errors.curatorIds}
                                </FormHelperText>
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'course' })}</H6>

                                <SingleSelect
                                    id="courseId"
                                    name="courseId"
                                    placeholder={formatMessage({
                                        id: 'course',
                                    })}
                                    value={formik.values.courseId}
                                    menuItems={courses}
                                    handleChange={handleCourseChange}
                                    handleBlur={formik.handleBlur}
                                    handleError={Boolean(
                                        formik.errors.courseId,
                                    )}
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.courseId &&
                                        formik.errors.courseId}
                                </FormHelperText>
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'language' })}</H6>

                                <SingleSelect
                                    id="languageEnum"
                                    name="languageEnum"
                                    placeholder={formatMessage({
                                        id: 'language',
                                    })}
                                    value={formik.values.languageEnum}
                                    handleChange={handleLanguageChange}
                                    menuItems={[
                                        { id: 'RU', name: 'RU' },
                                        { id: 'KZ', name: 'KZ' },
                                    ]}
                                />
                            </Box>
                        </form>
                    </DialogContent>
                )}

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={() => setOpen(false)}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isUpdating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={isUpdating}
                        onClick={() => formik.handleSubmit()}
                    >
                        {isUpdating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default EditGroupDialog
